import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/select-org/")({
  beforeLoad: ({ context }) => {
    if (!context.userId) {
      throw redirect({ to: "/sign-in" });
    }
    // This entry-point screen only exists on the primary app origin. On an org
    // subdomain there is exactly one org to enter, so go straight to the app.
    if (context.subdomain) {
      throw redirect({ to: "/app" });
    }
  },
  component: SelectOrgPage,
});

/**
 * Build the URL to enter an org by subdomain, mirroring the current environment
 * (prod: <sub>.afinio.co, local dev: <sub>.localhost:<port>).
 */
function orgAppUrl(subdomain: string): string {
  const { protocol, hostname, port } = window.location;
  const isLocal = hostname === "localhost" || hostname.endsWith(".localhost");
  const base = isLocal ? "localhost" : "afinio.co";
  const portPart = port ? `:${port}` : "";
  return `${protocol}//${subdomain}.${base}${portPart}/app`;
}

type Org = {
  companyId: string;
  name: string;
  subdomain: string | null;
  role: string;
  onboardingCompleted: boolean;
};

function goToOrg(org: Org) {
  if (org.subdomain) {
    window.location.href = orgAppUrl(org.subdomain);
  } else {
    // Org has no subdomain yet (created but not onboarded): finish setup on the
    // primary origin; the authenticated layout routes to onboarding.
    window.location.href = "/app";
  }
}

function SelectOrgPage() {
  const { data: orgs, isLoading } = useQuery(convexQuery(api.users.getMyOrganizations, {}));
  const [selected, setSelected] = useState<string | null>(null);

  // 0 orgs → nothing to enter; send them to create one. 1 org → enter it.
  useEffect(() => {
    if (!orgs) return;
    if (orgs.length === 0) {
      window.location.href = "/create";
    } else if (orgs.length === 1 && orgs[0]) {
      goToOrg(orgs[0]);
    }
  }, [orgs]);

  if (isLoading || !orgs || orgs.length <= 1) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
        <p className="text-sm text-muted-foreground">Taking you to your organization…</p>
      </div>
    );
  }

  const chosen = orgs.find((o) => o.companyId === selected) ?? null;

  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <div className="w-full max-w-[25rem] rounded-2xl border bg-card px-8 py-10 shadow-sm">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold tracking-tight">Choose an organization</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your account has access to multiple organizations. Pick the one you want to open.
          </p>
        </div>

        <Select value={selected ?? undefined} onValueChange={(v) => setSelected(v as string)}>
          <SelectTrigger className="w-full rounded-lg py-2.5 text-sm" size="default">
            {/* Map the selected value (companyId) back to the org name; without
                this the trigger renders the raw id. */}
            <SelectValue placeholder="Select an organization">
              {(value) => orgs.find((o) => o.companyId === value)?.name ?? "Select an organization"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {orgs.map((org) => (
              <SelectItem key={org.companyId} value={org.companyId} className="text-sm">
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          className="mt-4 w-full"
          disabled={!chosen}
          onClick={() => chosen && goToOrg(chosen)}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
